import { CONSUMER, TRADE } from 'dibs-buyer-layout/exports/userTypeEnums';
import { BILLBOARD_AD_KEY, HOLIDAY_COLLECTION_SEGMENT, TILE_AD_KEY } from '../constants';
import {
    type AdKey,
    type UserType,
    type AdData,
    type AdProductTile,
    type AdSkyscraper,
    type AdBillboard,
} from '../types';
import { sbAdExcludeMap, sbAdMap } from './sbAdMap';
import { sbTradeAdMap } from './sbTradeAdMap';

const CONSUMER_AD_KEYS = Object.keys(sbAdMap);
const TRADE_AD_KEYS = Object.keys(sbTradeAdMap);

const getUriRefSegments = (uriRef: string): string[] =>
    (uriRef || '')
        .split('?')[0]
        .split('/')
        .filter(segment => segment !== '');

export const getAdData = ({
    uriRef,
    adKey,
    userType,
}: {
    uriRef: string;
    adKey: AdKey;
    userType: UserType;
}): AdData | null => {
    let segments = getUriRefSegments(uriRef);
    const lastSegment = segments[segments.length - 1];

    // holiday collection tile ads should show only when no filters applied
    if (
        adKey === TILE_AD_KEY &&
        segments.includes(HOLIDAY_COLLECTION_SEGMENT) &&
        !lastSegment.includes(HOLIDAY_COLLECTION_SEGMENT)
    ) {
        segments = segments.filter(segment => segment !== HOLIDAY_COLLECTION_SEGMENT);
    }

    // run exclusion check: if a segment is present in the exclusion map, check the
    // adKey.  if they both match, return null
    if (
        segments.find(segment => sbAdExcludeMap[segment] && sbAdExcludeMap[segment].includes(adKey))
    ) {
        return null;
    }

    // if trade, use the trade segments before the consumer segments
    const key =
        (userType === TRADE ? segments.find(segment => TRADE_AD_KEYS.includes(segment)) : '') ||
        segments.find(segment => CONSUMER_AD_KEYS.includes(segment)) ||
        'default';

    if (userType === TRADE && sbTradeAdMap[key]) {
        return sbTradeAdMap[key];
    }
    return sbAdMap[key];
};

export function getProductTileAdData(
    uriRef: string,
    userType: UserType = CONSUMER
): AdProductTile[] {
    const ad = getAdData({ uriRef, userType, adKey: TILE_AD_KEY });
    return ad?.[TILE_AD_KEY] || [];
}

export function getBillboardAdData(
    uriRef: string,
    userType: UserType = CONSUMER
): AdBillboard | null {
    const ad = getAdData({ uriRef, userType, adKey: BILLBOARD_AD_KEY });
    return ad?.[BILLBOARD_AD_KEY] || null;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function getSkyscraperAdData(uriRef: string): AdSkyscraper | null {
    // we don't use this any more, but there's still some code present
    // this allows for some cleanup
    return null;
}
